// Access grants belong to this tab. The session-cookie bridge must never persist these keys.
export const REPORT_ACCESS_PREFIX = 'reportAccess:';
export const getReportAccessToken = slug => sessionStorage.getItem(`${REPORT_ACCESS_PREFIX}${slug}`) || '';
export const saveReportAccessToken = (slug, token) => {
    if (token) sessionStorage.setItem(`${REPORT_ACCESS_PREFIX}${slug}`, token);
    else sessionStorage.removeItem(`${REPORT_ACCESS_PREFIX}${slug}`);
};
export const clearReportSession = () => {
    const grants = Object.keys(sessionStorage).filter(key => key.startsWith(REPORT_ACCESS_PREFIX)).map(key => [key, sessionStorage.getItem(key)]);
    sessionStorage.clear();
    grants.forEach(([key, value]) => sessionStorage.setItem(key, value));
};

const trustedBases = new Set(['https://api.digitalwerks.com/']);
export const buildReportAccessHeaders = (slug, initialHeaders) => {
    const headers = new Headers(initialHeaders);
    if (slug) {
        headers.set('X-Report-Slug', slug);
        const token = getReportAccessToken(slug);
        if (token) headers.set('X-Report-Access', token);
    }
    return headers;
};

const currentReportSlug = () => {
    if (window.location.pathname.startsWith('/go/')) {
        try { return decodeURIComponent(window.location.pathname.split('/')[2] || ''); }
        catch { return ''; }
    }
    return sessionStorage.getItem('reportSlug') || '';
};

export const installReportAccessFetch = baseUrl => {
    if (baseUrl) trustedBases.add(new URL(`${baseUrl.replace(/\/$/, '')}/`, window.location.origin).href);
    if (window.__reportAccessFetchInstalled) return;
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
        const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.origin);
        const trusted = [...trustedBases].some(base => url.href.startsWith(base));
        if (!trusted) return originalFetch(input, init);
        const initialHeaders = new Headers(init?.headers || (typeof input === 'object' && input.headers) || {});
        const slug = initialHeaders.get('X-Report-Slug') || sessionStorage.getItem('reportSlug');
        const headers = buildReportAccessHeaders(slug, initialHeaders);
        // Clone before the first fetch consumes a Request body so a refreshed grant
        // can replay this same scoped request once, including POST payloads.
        const retryInput = typeof input?.clone === 'function' ? input.clone() : input;
        let sentToken = headers.get('X-Report-Access') || '';
        let response = await originalFetch(input, { ...init, headers });
        if (url.pathname.replace(/\/$/, '').endsWith('/reportprofile/slug')) return response;
        for (let attempt = 0; response.status === 401 && attempt < 2; attempt++) {
            const data = await response.clone().json().catch(() => null);
            if (data?.error?.code !== 'report_password_required') break;
            const activeSlug = currentReportSlug();
            if (activeSlug && data.slug !== activeSlug) break;
            const currentToken = getReportAccessToken(data.slug);
            if (attempt === 0 && currentToken && currentToken !== sentToken) {
                // This request started before unlock. A late response must not erase
                // the newer grant or send the user back to the password prompt.
                const retryHeaders = buildReportAccessHeaders(data.slug, headers);
                sentToken = currentToken;
                response = await originalFetch(retryInput, { ...init, headers: retryHeaders });
                continue;
            }
            if (currentToken && currentToken !== sentToken) break;
            if (data.slug && currentToken === sentToken) saveReportAccessToken(data.slug, '');
            window.dispatchEvent(new CustomEvent('report-access-required', { detail: data }));
            break;
        }
        return response;
    };
    window.__reportAccessFetchInstalled = true;
};
